import React from "react";
import { observer } from "mobx-react-lite";
import { useRootStore } from "../../../../store/contexts";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SatelliteMachineListStore } from "../../store/SatelliteMachineListStore";
import { TextField, Box, Typography, IconButton, Tooltip } from "@mui/material";
import { ContentCopyIcon as CopyIcon } from "../../../../icons";

type SatelliteMachineListDialogsProps = {
  store: SatelliteMachineListStore;
};

/**
 * Componente que agrupa todos los dialogs del listado de SatelliteMachine.
 * Separado para mantener SatelliteMachineListPage más limpio.
 */
export const SatelliteMachineListDialogs: React.FC<SatelliteMachineListDialogsProps> =
  observer(({ store }) => {
    const { langStore } = useRootStore();

    return (
      <>
        {/* Dialog de error genérico */}
        <ErrorDialog
          open={store.errorDialogOpened}
          title={store.errorTitle || langStore.errorTitle}
          description={store.errorDescription}
          onClose={() => store.closeErrorDialog()}
        />

        {/* Dialog de confirmación de borrado individual: mientras consulta si se
          puede borrar, el Ok espera; si el registro está en uso, dice por qué y
          solo ofrece Cerrar. */}
        <ConfirmCancelDialog
          open={store.confirmDeleteDialogOpened}
          title={store.confirmDeleteTitle}
          description={store.confirmDeleteDescription}
          onConfirm={() =>
            store.deleteCheck === "blocked"
              ? store.closeConfirmDeleteDialog()
              : store.confirmOkDelete()
          }
          onCancel={() => store.closeConfirmDeleteDialog()}
          onClose={() => store.closeConfirmDeleteDialog()}
          confirmLabel={
            store.deleteCheck === "blocked" ? langStore.closeCommand : undefined
          }
          hideCancelButton={store.deleteCheck === "blocked"}
          confirmDisabled={
            store.deleteCheck === "checking" || store.deleteCheck === "deleting"
          }
          cancelDisabled={
            store.deleteCheck === "deleting" && !store.deleteStuck
          }
        />

        {/* Dialog de confirmación de suspensión de satellite */}
        <ConfirmCancelDialog
          open={store.suspendDialogOpen}
          title={langStore.satelliteSuspendTitle}
          description={langStore.satelliteSuspendDescription}
          onConfirm={() => store.confirmSuspendSatellite()}
          onCancel={() => store.closeSuspendDialog()}
          onClose={() => store.closeSuspendDialog()}
        >
          <TextField
            fullWidth
            label={langStore.satelliteReasonLabel}
            value={store.suspendDialogReason}
            onChange={(e) => store.setSuspendReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </ConfirmCancelDialog>

        {/* Dialog de confirmación de revocación de satellite */}
        <ConfirmCancelDialog
          open={store.revokeDialogOpen}
          title={langStore.satelliteRevokeTitle}
          description={langStore.satelliteRevokeDescription}
          onConfirm={() => store.confirmRevokeSatellite()}
          onCancel={() => store.closeRevokeDialog()}
          onClose={() => store.closeRevokeDialog()}
        >
          <TextField
            fullWidth
            label={langStore.satelliteReasonLabel}
            value={store.revokeDialogReason}
            onChange={(e) => store.setRevokeReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </ConfirmCancelDialog>

        {/* Dialog para mostrar el nuevo token generado */}
        <ConfirmCancelDialog
          open={store.tokenDialogOpen}
          title={langStore.satelliteTokenTitle}
          description={langStore.satelliteTokenDescription(
            store.tokenDialogSatelliteName
          )}
          onConfirm={() => store.closeTokenDialog()}
          onCancel={() => store.closeTokenDialog()}
          onClose={() => store.closeTokenDialog()}
          confirmLabel={langStore.closeCommand}
          hideCancelButton
        >
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", flex: 1, wordBreak: "break-all" }}
            >
              {store.tokenDialogValue}
            </Typography>
            <Tooltip title={langStore.satelliteCopyToken}>
              <IconButton
                size="small"
                onClick={() =>
                  navigator.clipboard.writeText(store.tokenDialogValue)
                }
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </ConfirmCancelDialog>
      </>
    );
  });
